'use client';

import * as React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { NavAgents } from '@/components/sidebar/nav-agents';
import { NavUserWithTeams } from '@/components/sidebar/nav-user-with-teams';
import { Logo } from '@/components/sidebar/logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUserInfoContext } from '@/contexts/user-info-context';
import EditUserNameModal from '../deep-mining/user-center/edit-user-name/edit-user-name';
import ChangePasswordModal from '../deep-mining/user-center/change-password/change-password';
import ConfirmLogoutSpaceModal from '@/components/deep-mining/user-center/confirm-logout-space/confirm-logout-space';
import { siteNameUpperCase } from '@/lib/site';
import PrivacyAgreementModal from '../deep-mining/user-center/privacy-agreement/privacy-agreement';
import { useStores } from '@/store/hooks/useStore';
import { useI18n } from '@/hooks/use-i18n';

export function SidebarLeft({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { t } = useI18n('components');
  const stores = useStores();
  const permissions = stores.userStore.permissions;
  const { state, setOpen } = useSidebar();
  const { userInfo, updateUserInfo } = useUserInfoContext();

  const [editUserNameVisible, setEditUserNameVisible] = React.useState(false);
  const [changePasswordVisible, setChangePasswordVisible] =
    React.useState(false);
  const [confirmLogoutSpaceVisible, setConfirmLogoutSpaceVisible] =
    React.useState(false);
  const [privacyAgreementVisible, setPrivacyAgreementVisible] =
    React.useState(false);

  // Handle keyboard shortcuts (CMD+B) for consistency
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        // We'll handle this in the parent page component
        // to ensure proper coordination between panels
        setOpen(!state.startsWith('expanded'));

        // Broadcast a custom event to notify other components
        window.dispatchEvent(
          new CustomEvent('sidebar-left-toggled', {
            detail: { expanded: !state.startsWith('expanded') },
          }),
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, setOpen]);

  const isCollapsed = state === 'collapsed';
  return (
    <Sidebar
      collapsible="icon"
      className="border-r-0 bg-[#F4F4F5] backdrop-blur-sm [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
      {...props}
    >
      <SidebarHeader className="px-2 py-2">
        <div className="flex h-[40px] items-center px-1 relative">
          <Link href="/dashboard">
            <Logo />
          </Link>
          {!isCollapsed && (
            <div className="ml-2 transition-all duration-200 ease-in-out whitespace-nowrap">
              <span className="font-semibold">{siteNameUpperCase}</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {!isCollapsed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarTrigger className="h-8 w-8 cursor-pointer hover:bg-gray-200 hover:text-gray-700 transition-colors duration-200" />
                </TooltipTrigger>
                <TooltipContent className="cursor-default">
                  {t('sidebar.collapse')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        {isCollapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger
                className="h-8 w-8 cursor-pointer hover:bg-gray-200 hover:text-gray-700 transition-colors duration-200"
                triggerType="right"
              />
            </TooltipTrigger>
            <TooltipContent className="cursor-default">
              {t('sidebar.expand')}
            </TooltipContent>
          </Tooltip>
        )}
      </SidebarHeader>
      <SidebarContent className="[&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
        <NavAgents />
      </SidebarContent>
      <SidebarFooter>
        {permissions?.includes('user_center') && (
          <NavUserWithTeams
            userInfo={userInfo}
            collapsed={isCollapsed}
            onChangeUserName={() => setEditUserNameVisible(true)}
            onChangePassword={() => setChangePasswordVisible(true)}
            handleLogoutSpace={() => setConfirmLogoutSpaceVisible(true)}
            onOpenPrivacyAgreement={() => setPrivacyAgreementVisible(true)}
          />
        )}
      </SidebarFooter>
      {editUserNameVisible && (
        <EditUserNameModal
          userInfo={userInfo}
          updateUserInfo={updateUserInfo}
          open={editUserNameVisible}
          onClose={() => setEditUserNameVisible(false)}
        />
      )}
      {changePasswordVisible && (
        <ChangePasswordModal
          account={userInfo?.email || userInfo?.phone || ''}
          open={changePasswordVisible}
          onClose={() => setChangePasswordVisible(false)}
        />
      )}
      {confirmLogoutSpaceVisible && (
        <ConfirmLogoutSpaceModal
          userInfo={userInfo}
          open={confirmLogoutSpaceVisible}
          onClose={() => setConfirmLogoutSpaceVisible(false)}
        />
      )}
      {privacyAgreementVisible && (
        <PrivacyAgreementModal
          open={privacyAgreementVisible}
          onClose={() => setPrivacyAgreementVisible(false)}
        />
      )}
    </Sidebar>
  );
}
